import {
  type FlatSurface,
  GEOMETRY_TOLERANCE,
  isPlacementInsideSurface,
  type PlacementPosition,
  type SizeMm,
  type SurfaceOutline,
  type SurfacePoint,
} from '@repo/api-contracts';
import {
  componentRectOnPhoto,
  flatScale,
  photoToSurfaceMm,
  surfaceMmToPhoto,
} from '@repo/design-renderer';

export { componentRectOnPhoto, flatScale, photoToSurfaceMm, surfaceMmToPhoto };

export interface PhotoDimensions {
  widthPx: number;
  heightPx: number;
}
export interface PhotoRect {
  x: number;
  y: number;
  width: number;
  height: number;
}
/**
 * 사진 px 점을 차례로 이은 선. 축 정렬 사각형(PhotoRect)으로 나타낼 수 없는 곡면 경계·격자선을 표본 점으로 잇는다.
 * 닫힌 도형이면 마지막 점에서 첫 점으로 닫고, 첫 점을 반복하지 않는다.
 */
export type PhotoPolyline = readonly SurfacePoint[];
export const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));
/** 사진 % 값을 사진 안(0–100)으로 자른다. 평면·원통 표면 조작이 같은 범위를 쓴다. */
export const clampPercent = (value: number): number => clamp(value, 0, 100);

export const rectPoints = (
  left: number,
  top: number,
  right: number,
  bottom: number,
): FlatSurface['points'] => [
  { x: left, y: top },
  { x: right, y: top },
  { x: right, y: bottom },
  { x: left, y: bottom },
];

/** 윤곽 점을 옛 틀에서 새 틀로 같은 비율로 옮긴다. 부동소수 오차는 사진 범위로 자른다. */
export const fitOutlineToRect = (
  outline: SurfaceOutline,
  from: FlatSurface['points'],
  to: FlatSurface['points'],
): SurfaceOutline => {
  const [fromTl, , fromBr] = from;
  const [toTl, , toBr] = to;
  const fromWidth = fromBr.x - fromTl.x;
  const fromHeight = fromBr.y - fromTl.y;
  // 옛 사각형의 폭·높이가 거의 0이면 그 축엔 지킬 비율이 없다 — 나눗셈(→NaN) 대신 스케일을 0으로 두어
  // 그 축의 모든 점을 새 사각형의 시작 변(toTl)으로 모은다.
  const scaleX = fromWidth > GEOMETRY_TOLERANCE ? (toBr.x - toTl.x) / fromWidth : 0;
  const scaleY = fromHeight > GEOMETRY_TOLERANCE ? (toBr.y - toTl.y) / fromHeight : 0;

  return {
    points: outline.points.map((point) => ({
      x: clampPercent(toTl.x + (point.x - fromTl.x) * scaleX),
      y: clampPercent(toTl.y + (point.y - fromTl.y) * scaleY),
    })),
  };
};

/** Invert the actual SVG screen matrix, including fit, zoom, page offset and scroll. */
export const clientToPhotoPoint = (
  svg: SVGSVGElement,
  point: SurfacePoint,
): SurfacePoint | null => {
  const matrix = svg.getScreenCTM();
  if (matrix === null) return null;
  const { a, b, c, d, e, f } = matrix;
  const determinant = a * d - b * c;
  if (!Number.isFinite(determinant) || determinant === 0) return null;
  const x = ((point.x - e) * d - (point.y - f) * c) / determinant;
  const y = ((point.y - f) * a - (point.x - e) * b) / determinant;
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

export const photoToPercent = (photo: PhotoDimensions, point: SurfacePoint): SurfacePoint => ({
  x: clampPercent((point.x / photo.widthPx) * 100),
  y: clampPercent((point.y / photo.heightPx) * 100),
});

export const clampPlacementPosition = (
  component: SizeMm,
  surface: SizeMm,
  position: PlacementPosition,
): PlacementPosition | null => {
  if (!isPlacementInsideSurface(component, surface, { xMm: 0, yMm: 0 })) return null;
  return {
    xMm: clamp(position.xMm, 0, Math.max(0, surface.widthMm - component.widthMm)),
    yMm: clamp(position.yMm, 0, Math.max(0, surface.heightMm - component.heightMm)),
  };
};
